import { OperatorStartupSnapshot } from "@maito/client-runtime/state/operator-startup";
import * as Schema from "effect/Schema";

const DATABASE = "maito:operator-startup:v1";
const STORE = "workspaces";
const MAX_CHARACTERS = 128_000;
const MAX_AGE_MS = 7 * 24 * 60 * 60_000;
const Record = Schema.Struct({
  key: Schema.String,
  savedAt: Schema.Number,
  snapshot: OperatorStartupSnapshot,
});
let writeGeneration = 0;
const decode = Schema.decodeUnknownSync(Schema.fromJsonString(Record));

function openDatabase(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    let settled = false;
    const finish = (database: IDBDatabase | null) => {
      if (settled) {
        database?.close();
        return;
      }
      settled = true;
      clearTimeout(timeout);
      resolve(database);
    };
    const timeout = setTimeout(() => finish(null), 1_500);
    try {
      const request = indexedDB.open(DATABASE, 1);
      request.addEventListener("upgradeneeded", () => request.result.createObjectStore(STORE));
      request.addEventListener("error", () => finish(null));
      request.addEventListener("blocked", () => finish(null));
      request.addEventListener("success", () => finish(request.result));
    } catch {
      finish(null);
    }
  });
}

export function decodeOperatorCache(
  raw: unknown,
  key: string,
  now = Date.now(),
): OperatorStartupSnapshot | null {
  if (typeof raw !== "string" || raw.length > MAX_CHARACTERS) return null;
  try {
    const record = decode(raw);
    if (record.key !== key || record.savedAt > now || now - record.savedAt > MAX_AGE_MS)
      return null;
    return record.snapshot;
  } catch {
    return null;
  }
}

export async function readOperatorCache(key: string): Promise<OperatorStartupSnapshot | null> {
  const database = await openDatabase();
  if (database === null) return null;
  return new Promise((resolve) => {
    const finish = (value: OperatorStartupSnapshot | null) => {
      database.close();
      resolve(value);
    };
    try {
      const request = database.transaction(STORE, "readonly").objectStore(STORE).get(key);
      request.addEventListener("success", () => finish(decodeOperatorCache(request.result, key)));
      request.addEventListener("error", () => finish(null));
    } catch {
      finish(null);
    }
  });
}

export async function writeOperatorCache(
  key: string,
  snapshot: OperatorStartupSnapshot,
): Promise<void> {
  const generation = writeGeneration;
  const raw = JSON.stringify({ key, savedAt: Date.now(), snapshot });
  if (raw.length > MAX_CHARACTERS) return;
  const database = await openDatabase();
  if (database === null) return;
  if (generation !== writeGeneration) {
    database.close();
    return;
  }
  await new Promise<void>((resolve) => {
    const finish = () => {
      database.close();
      resolve();
    };
    try {
      const transaction = database.transaction(STORE, "readwrite");
      transaction.addEventListener("complete", finish);
      transaction.addEventListener("error", finish);
      transaction.addEventListener("abort", finish);
      const store = transaction.objectStore(STORE);
      store.put(raw, key);
      const keys = store.getAllKeys();
      keys.addEventListener("success", () => {
        // Keep storage bounded across accounts and environments; evictions are cache misses.
        for (const oldKey of keys.result
          .filter((value) => value !== key)
          .slice(0, Math.max(0, keys.result.length - 8)))
          store.delete(oldKey);
      });
    } catch {
      finish();
    }
  });
}

export async function clearOperatorCache(): Promise<void> {
  writeGeneration += 1;
  const database = await openDatabase();
  if (database === null) return;
  await new Promise<void>((resolve) => {
    const finish = () => {
      database.close();
      resolve();
    };
    try {
      const transaction = database.transaction(STORE, "readwrite");
      transaction.objectStore(STORE).clear();
      transaction.addEventListener("complete", finish);
      transaction.addEventListener("error", finish);
      transaction.addEventListener("abort", finish);
    } catch {
      finish();
    }
  });
}
